import {LRUMap} from 'lru_map'
import {memo, useEffect, useRef} from 'react'
import type {Iri} from '../../@types/Api'
import {MERCURE_ENDPOINT} from '../../config'
import {useEffectEvent} from '../../hooks/useEffectEvent'
import {useMutateTags} from '../../hooks/useMutate'
import {NotificationNewReleaseEvent} from '../../utils/event'
import eventDispatcher from '../../utils/eventDispatcher'

const cache = new LRUMap<Iri, boolean>(1000)

type Update = {
  readonly type: undefined
  readonly '@id': string
  readonly tags: string[]
}
type Notification = {
  readonly type: 'notification'
  readonly event: 'new-release'
}

type Message = Update | Notification

interface Props {
  topics: Iri[]
}

export const Mercure = memo(({topics}: Props) => {
  const eventSource = useRef<EventSource>(undefined)
  const mutateTags = useMutateTags()
  const isLighthouse = navigator.userAgent.indexOf('Chrome-Lighthouse') >= 0

  const handleUpdate = useEffectEvent((data: Update): void => {
    if (cache.has(data['@id'])) {
      return
    }
    cache.set(data['@id'], true)
    if (data.tags) {
      setTimeout(() => {
        mutateTags(data.tags)
      }, 10)
    }
  })

  const handleNotification = useEffectEvent((data: Notification): void => {
    if (data.event === 'new-release') {
      eventDispatcher.dispatchEvent(new NotificationNewReleaseEvent())
    } else {
      throw new Error(`Unexpected Notification event: ${data.event}`)
    }
  })

  useEffect(() => {
    if (topics.length < 1) {
      return
    }
    if (isLighthouse) {
      return
    }
    eventSource.current?.close()

    const url = new URL(`${MERCURE_ENDPOINT}/.well-known/mercure`)
    for (const topic of topics) {
      url.searchParams.append('topic', topic)
    }

    eventSource.current = new EventSource(url, {withCredentials: true})
    eventSource.current.onmessage = (event: MessageEvent) => {
      const data = JSON.parse(event.data) as Message
      if (data.type === 'notification') {
        return handleNotification(data)
      }

      if (data['@id']) {
        return handleUpdate(data)
      }

      throw new Error('Unexpected mercure response')
    }

    return () => {
      if (eventSource.current) {
        eventSource.current.onmessage = null
        eventSource.current.close()
      }
    }
  }, [topics, handleNotification, handleUpdate, isLighthouse])

  return null
})
